import { ReactNode } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { PageCard } from '../layout/page/PageCard';
import { PlanningCycle } from '../../services/strategicPlanningApi';
import { PLANNING_OBJECTIVE_KINDS } from '../../types/PlanningObjectiveKind';

export type PlanningFilterTooltips = {
  cycle?: string;
  objectiveKind?: string;
  objective?: string;
  meta?: string;
  marco?: string;
};

type PlanningFiltersBarProps = {
  cycles: PlanningCycle[];
  selectedCycleFilter: string;
  onCycleFilterChange: (value: string) => void;
  selectedObjectiveKindFilter?: string;
  onObjectiveKindFilterChange?: (value: string) => void;
  showObjectiveKindFilter?: boolean;
  selectedObjectiveFilter?: string;
  onObjectiveFilterChange?: (value: string) => void;
  objectiveOptions?: Array<{ id: string; label: string }>;
  showObjectiveFilter?: boolean;
  selectedMetaFilter?: string;
  onMetaFilterChange?: (value: string) => void;
  metaOptions?: Array<{ id: string; label: string }>;
  showMetaFilter?: boolean;
  selectedMarcoFilter?: string;
  onMarcoFilterChange?: (value: string) => void;
  marcoOptions?: Array<{ id: string; label: string }>;
  showMarcoFilter?: boolean;
  filterTooltips?: PlanningFilterTooltips;
  counterLabel: string;
};

export function PlanningFiltersBar({
  cycles,
  selectedCycleFilter,
  onCycleFilterChange,
  selectedObjectiveKindFilter = '',
  onObjectiveKindFilterChange,
  showObjectiveKindFilter = false,
  selectedObjectiveFilter = '',
  onObjectiveFilterChange,
  objectiveOptions = [],
  showObjectiveFilter = false,
  selectedMetaFilter = '',
  onMetaFilterChange,
  metaOptions = [],
  showMetaFilter = false,
  selectedMarcoFilter = '',
  onMarcoFilterChange,
  marcoOptions = [],
  showMarcoFilter = false,
  filterTooltips,
  counterLabel,
}: PlanningFiltersBarProps) {
  return (
    <PageCard>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <FilterSelect
            label="Ciclo"
            value={selectedCycleFilter}
            onChange={onCycleFilterChange}
            tooltip={filterTooltips?.cycle}
          >
            <option value="">Todos os ciclos</option>
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.name}
              </option>
            ))}
          </FilterSelect>

          {showObjectiveKindFilter && onObjectiveKindFilterChange ? (
            <FilterSelect
              label="Tipo de objetivo"
              value={selectedObjectiveKindFilter}
              onChange={onObjectiveKindFilterChange}
              tooltip={filterTooltips?.objectiveKind}
            >
              <option value="">Todos os tipos</option>
              {PLANNING_OBJECTIVE_KINDS.map((kind) => (
                <option key={kind.id} value={kind.id}>
                  {kind.label}
                </option>
              ))}
            </FilterSelect>
          ) : null}

          {showObjectiveFilter && onObjectiveFilterChange ? (
            <FilterSelect
              label="Objetivo"
              value={selectedObjectiveFilter}
              onChange={onObjectiveFilterChange}
              tooltip={filterTooltips?.objective}
            >
              <option value="">Todos os objetivos</option>
              {objectiveOptions.map((objective) => (
                <option key={objective.id} value={objective.id}>
                  {objective.label}
                </option>
              ))}
            </FilterSelect>
          ) : null}

          {showMetaFilter && onMetaFilterChange ? (
            <FilterSelect
              label="Meta"
              value={selectedMetaFilter}
              onChange={onMetaFilterChange}
              tooltip={filterTooltips?.meta}
            >
              <option value="">Todas as metas</option>
              {metaOptions.map((meta) => (
                <option key={meta.id} value={meta.id}>
                  {meta.label}
                </option>
              ))}
            </FilterSelect>
          ) : null}

          {showMarcoFilter && onMarcoFilterChange ? (
            <FilterSelect
              label="Marco"
              value={selectedMarcoFilter}
              onChange={onMarcoFilterChange}
              tooltip={filterTooltips?.marco}
            >
              <option value="">Todos os marcos</option>
              {marcoOptions.map((marco) => (
                <option key={marco.id} value={marco.id}>
                  {marco.label}
                </option>
              ))}
            </FilterSelect>
          ) : null}
        </div>
        <p className="text-sm text-secondary-600">{counterLabel}</p>
      </div>
    </PageCard>
  );
}

type FilterSelectProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  tooltip?: string;
  children: ReactNode;
};

function FilterSelect({ label, value, onChange, tooltip, children }: FilterSelectProps) {
  return (
    <label className="block min-w-[220px] max-w-sm">
      <span className="erp-label inline-flex items-center gap-1">
        {label}
        {tooltip ? (
          <span title={tooltip} className="cursor-help text-secondary-400" aria-label={tooltip}>
            <QuestionMarkCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          </span>
        ) : null}
      </span>
      <select
        className="erp-input"
        value={value}
        title={tooltip}
        onChange={(event) => onChange(event.target.value)}
      >
        {children}
      </select>
    </label>
  );
}
